import { useState, useEffect } from "react";
import api from "../../lib/apiClient";
import { cacheGet, cacheSet } from "../../lib/cache";

/**
 * useProfileData — bundles the three "static" data fetches a profile
 * page needs:
 *   1. /users/{username}            → profile doc (cached, SWR)
 *   2. /users/{username}/videos/highlighted → highlighted slice
 *   3. /videos/featured             → global featured (owner's videos
 *                                     prepended), used by the player
 *                                     overlay's Featured panel
 *   4. /users/{username}/channel-chat → channel chat messages
 *
 * The playback-coupled effects (comments, nearby videos, requestedVideoId
 * resolution) stay inline in ProfilePage because they need to mutate
 * parent-owned PlayerContext state.
 *
 * Stale-while-revalidate: cached profile is painted immediately, then
 * refreshed from the network in the background. Promise.allSettled
 * keeps a flaky `highlighted` endpoint from breaking the whole page.
 *
 * Inputs:
 *   username        — route param. When `isReservedSlug` is true, the
 *                     hook is a strict no-op (returns initial state).
 *   token           — JWT, passed as Bearer header on the profile fetch
 *                     so `is_following` resolves correctly.
 *   isReservedSlug  — `/login`, `/admin`, etc. — never fetch for these.
 *
 * Returns: { profile, setProfile, loading, highlightedVideos,
 *            followersCount, setFollowersCount, following, setFollowing,
 *            featuredVideos, channelChat, setChannelChat }
 */
export function useProfileData({ username, token, isReservedSlug }) {
  const [profile, setProfile] = useState(null);
  const [highlightedVideos, setHighlightedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followersCount, setFollowersCount] = useState(0);
  const [following, setFollowing] = useState(false);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [channelChat, setChannelChat] = useState([]);

  // 1+2: Profile + highlighted videos (SWR).
  useEffect(() => {
    if (isReservedSlug || !username) return;
    const profileKey = `profile:${username.toLowerCase()}`;
    const fetchProfile = async () => {
      const cached = cacheGet(profileKey);
      if (cached) {
        setProfile(cached.profile);
        setHighlightedVideos(cached.highlighted);
        setFollowing(cached.profile.is_following);
        setFollowersCount(cached.profile.followers_count);
        setLoading(false);
      }
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // Promise.allSettled (not all) so a transient failure of the
        // highlighted-videos endpoint doesn't prevent the profile from
        // rendering — before the fix, the user saw "User not found"
        // for valid users on a single 500.
        const [profileRes, highlightedRes] = await Promise.allSettled([
          api.get(`/users/${username}`, { headers }),
          api.get(`/users/${username}/videos/highlighted`),
        ]);
        if (profileRes.status === "fulfilled") {
          setProfile(profileRes.value.data);
          setFollowing(profileRes.value.data.is_following);
          setFollowersCount(profileRes.value.data.followers_count);
          const highlighted = highlightedRes.status === "fulfilled" ? highlightedRes.value.data : [];
          setHighlightedVideos(highlighted);
          cacheSet(profileKey, { profile: profileRes.value.data, highlighted });
        } else if (!cached) {
          console.error(profileRes.reason);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchProfile();
  }, [username, token, isReservedSlug]);

  // 3: Featured — stacks the profile owner's videos first so shareable
  // profiles highlight the owner's content before global featured.
  // Featured global list is SWR-cached to avoid double-fetches when
  // bouncing between Home and Profile pages.
  useEffect(() => {
    if (!profile) return;
    const fetchFeatured = async () => {
      const cachedFeatured = cacheGet("featured");
      const stitch = (globalFeatured) => {
        const ownVideos = profile?.videos || [];
        const ownIds = new Set(ownVideos.map((v) => v.id));
        const rest = globalFeatured.filter((v) => !ownIds.has(v.id));
        setFeaturedVideos([...ownVideos, ...rest]);
      };
      if (cachedFeatured) stitch(cachedFeatured);
      try {
        const res = await api.get(`/videos/featured`);
        const list = res.data || [];
        cacheSet("featured", list);
        stitch(list);
      } catch (err) {
        console.error("Failed to fetch featured videos:", err);
      }
    };
    fetchFeatured();
  }, [profile]);

  // 4: Channel chat — SWR-cached per profile so revisiting the same
  // channel is instant.
  useEffect(() => {
    if (!profile) return;
    const key = `channelChat:${profile.username.toLowerCase()}`;
    const fetchChannelChat = async () => {
      const cached = cacheGet(key);
      if (cached) setChannelChat(cached);
      try {
        const res = await api.get(`/users/${profile.username}/channel-chat`);
        setChannelChat(res.data);
        cacheSet(key, res.data);
      } catch (err) {
        console.error("Failed to fetch channel chat:", err);
        if (!cached) setChannelChat([]);
      }
    };
    fetchChannelChat();
  }, [profile]);

  return {
    profile, setProfile,
    loading,
    highlightedVideos,
    followersCount, setFollowersCount,
    following, setFollowing,
    featuredVideos,
    channelChat, setChannelChat,
  };
}
